import { Stack } from "@mui/material";
import type { FC } from "react";
import {
	type ClientPortfolioDetailCommodityResponseDTO,
	ClientPortfolioInvestmentEnterFeeAmountTypeEnum,
	ClientPortfolioInvestmentEnterFeeApplyingEnum,
	ClientPortfolioInvestmentEnterFeeTypeEnum,
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioInvestmentTypeEnum,
	ClientPortfolioPaymentFrequencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getCurrencySymbol } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { DataRowCollapsible } from "web_myplann/Shared/Components/DataRowCollapsible";
import { CurrencyCode } from "web_myplann/Shared/constants";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { numberFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";

type Props = {
	commodities: ClientPortfolioDetailCommodityResponseDTO[];
};

export const CommoditySection: FC<Props> = ({ commodities }) => {
	const { t } = useResource();

	const collapseRowLabel = (
		commodity: ClientPortfolioDetailCommodityResponseDTO,
	) =>
		`${t(Resources.PortfolioDetail.ToolHistory.ActionType[commodity.investmentType ?? ClientPortfolioInvestmentTypeEnum.Buy])} - ${dateFormatter(commodity.startAt)}`;

	const entryTransactionAmount = (
		commodity: ClientPortfolioDetailCommodityResponseDTO,
	) => {
		if (!commodity.enterFee?.amount) {
			return EMPTY_DETAIL_FIELD;
		}

		const formattedAmount = numberFormatter.format(commodity.enterFee.amount);
		const isPercent =
			commodity.enterFee.amountType ===
			ClientPortfolioInvestmentEnterFeeAmountTypeEnum.Percent;
		const unit = isPercent
			? "%"
			: getCurrencySymbol(
					(commodity.currency as unknown as CurrencyCode) ?? CurrencyCode.CZK,
				);

		return `${formattedAmount} ${unit}`;
	};

	const fee = (commodity: ClientPortfolioDetailCommodityResponseDTO) => {
		if (!commodity.amount) {
			return EMPTY_DETAIL_FIELD;
		}

		const formattedAmount = numberFormatter.format(commodity.amount);
		const currencySymbol = getCurrencySymbol(
			(commodity.currency as unknown as CurrencyCode) ?? CurrencyCode.CZK,
		);
		const frequency = t(
			Resources.PortfolioDetail.ToolHistory.Fund.Frequency[
				commodity.frequency ?? ClientPortfolioPaymentFrequencyEnum.None
			],
		).toLowerCase();

		return `${formattedAmount} ${currencySymbol} ${frequency}`;
	};

	return (
		<Stack>
			{commodities.map((commodity, index) => {
				return (
					<DataRowCollapsible
						key={commodity.id}
						label={collapseRowLabel(commodity)}
						defaultExpanded={index === 0}
					>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.type)}
							value={t(
								Resources.PortfolioDetail.ToolHistory.ActionType[
									commodity.investmentType ??
										ClientPortfolioInvestmentTypeEnum.Buy
								],
							)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.startDate)}
							value={dateFormatter(commodity.startAt)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.endDate)}
							value={dateFormatter(commodity.endAt)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.amount)}
							value={fee(commodity)}
						/>
						{commodity.investmentType ===
							ClientPortfolioInvestmentTypeEnum.Buy && (
							<>
								<DataRow
									label={t(Resources.PortfolioDetail.ToolHistory.Fund.spread)}
									value={`${commodity.spread} %`}
								/>
								{commodity.enterFee?.type &&
									commodity.investmentType ===
										ClientPortfolioInvestmentTypeEnum.Buy && (
										<DataRow
											label={t(
												Resources.PortfolioDetail.ToolHistory.Fund
													.entryTransaction,
											)}
											value={t(
												Resources.PortfolioDetail.ToolHistory.Fund
													.EntryTransaction[commodity.enterFee?.type],
											)}
										/>
									)}
								<DataRow
									label={t(
										Resources.PortfolioDetail.ToolHistory.Fund
											.entryTransactionAmount,
									)}
									value={entryTransactionAmount(commodity)}
								/>
								{commodity.enterFee?.chargedFeeFromFirstPayments && (
									<DataRow
										label={t(
											Resources.PortfolioDetail.ToolHistory.Fund
												.entryTransactionProcantage,
										)}
										value={`${commodity.enterFee?.chargedFeeFromFirstPayments} %`}
									/>
								)}
								{commodity.enterFee?.type ===
									ClientPortfolioInvestmentEnterFeeTypeEnum.Once &&
									commodity.frequency ===
										ClientPortfolioInvestmentFrequencyEnum.Once && (
										<DataRow
											label={t(
												Resources.PortfolioDetail.ToolHistory.Fund.applying,
											)}
											value={t(
												Resources.PortfolioDetail.ToolHistory.Fund.Applying[
													commodity.enterFee?.applying ??
														ClientPortfolioInvestmentEnterFeeApplyingEnum.ChargedFeeFromInvestment
												],
											)}
										/>
									)}
							</>
						)}
					</DataRowCollapsible>
				);
			})}
		</Stack>
	);
};
